import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Download, Calendar, FileText, ExternalLink, Share } from 'lucide-react';
import { useUserCertificates } from '../../hooks';
import { certificateService } from '../../services/api';
import { Card, LoadingSpinner, Button, Badge, EmptyState } from '../../components/common';
import { Certificate } from '../../types';
import toast from 'react-hot-toast';

const CertificatesPage: React.FC = () => {
  const { data: certificatesData, isLoading, refetch } = useUserCertificates();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const certificates = certificatesData?.certificates || [];

  const downloadCertificate = async (certificateId: string, userName: string, categoryName: string) => {
    try {
      setDownloadingId(certificateId);
      
      const blob = await certificateService.download(certificateId);
      
      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificado_${userName}_${categoryName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificado descargado exitosamente');
      refetch(); // Actualizar estadísticas de descarga
      
    } catch (error) {
      console.error('Error descargando certificado:', error);
      toast.error('Error al descargar el certificado');
    } finally {
      setDownloadingId(null);
    }
  };

  const shareCertificate = (certificate: Certificate) => {
    const categoryName = certificate.category?.name || 'una categoría';
    
    if (navigator.share) {
      navigator.share({
        title: `Certificado de ${categoryName}`,
        text: `He completado el curso de ${categoryName} en Universidad BrinsAprende`,
        url: window.location.origin + '/certificates'
      });
    } else {
      // Fallback: copiar al portapapeles
      const text = `¡He completado el curso de ${categoryName} en Universidad BrinsAprende! 🎓`;
      navigator.clipboard.writeText(text);
      toast.success('Texto copiado al portapapeles');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-3xl font-bold text-brinsa-azul mb-4">
          Mis Certificados
        </h1>
        <p className="text-lg text-brinsa-azulOscuro/80">
          Descarga y comparte tus logros académicos
        </p>
      </motion.div>

      {/* Estadísticas */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-brinsa-amarillo/15 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Award className="w-6 h-6 text-brinsa-amarillo" />
            </div>
            <div className="text-2xl font-bold text-brinsa-azul mb-1">
              {certificates.length}
            </div>
            <div className="text-sm text-brinsa-azulOscuro/80">Certificados obtenidos</div>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-brinsa-verde/15 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Download className="w-6 h-6 text-brinsa-verde" />
            </div>
            <div className="text-2xl font-bold text-brinsa-azul mb-1">
              {certificates.reduce((sum, cert) => sum + (cert.downloadCount || 0), 0)}
            </div>
            <div className="text-sm text-brinsa-azulOscuro/80">Descargas totales</div>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-brinsa-celeste/15 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-brinsa-celeste" />
            </div>
            <div className="text-2xl font-bold text-brinsa-azul mb-1">
              {certificates.length > 0 
                ? (() => {
                    const validDates = certificates
                      .map(c => c.issuedDate)
                      .filter(date => date)
                      .map(date => new Date(date).getTime());
                    
                    return validDates.length > 0
                      ? new Date(Math.max(...validDates)).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })
                      : '-';
                  })()
                : '-'
              }
            </div>
            <div className="text-sm text-brinsa-azulOscuro/80">Último certificado</div>
          </Card>
        </div>
      </motion.div>

      {/* Lista de certificados */}
      {certificates.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-4"
        >
          {certificates.map((certificate, index) => (
            <motion.div
              key={certificate.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      {/* Icono del certificado */}
                      <div className="w-16 h-16 bg-brinsa-amarillo rounded-lg flex items-center justify-center flex-shrink-0">
                        <Award className="w-8 h-8 text-white" />
                      </div>

                      {/* Información del certificado */}
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-brinsa-azul mb-2">
                          Certificado de Finalización
                        </h3>
                        <p className="text-lg text-brinsa-azul mb-2">
                          {certificate.category?.name || 'Categoría no disponible'}
                        </p>
                        <p className="text-sm text-brinsa-azulOscuro/85 mb-3">
                          {certificate.category?.description || 'Descripción no disponible'}
                        </p>
                        
                        <div className="flex items-center space-x-4 text-sm text-brinsa-azulOscuro/70">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            Emitido el {new Date(certificate.issuedDate).toLocaleDateString('es-ES')}
                          </div>
                          <div className="flex items-center">
                            <Download className="w-4 h-4 mr-1" />
                            {certificate.downloadCount || 0} {(certificate.downloadCount || 0) === 1 ? 'descarga' : 'descargas'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex flex-col space-y-2">
                      <Button
                        size="sm"
                        onClick={() => downloadCertificate(
                          certificate.id, 
                          certificate.user?.name || 'Usuario', 
                          certificate.category?.name || 'Categoria'
                        )}
                        isLoading={downloadingId === certificate.id}
                        leftIcon={<Download className="w-4 h-4" />}
                      >
                        Descargar PDF
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => shareCertificate(certificate)}
                        leftIcon={<Share className="w-4 h-4" />}
                      >
                        Compartir
                      </Button>
                    </div>
                  </div>

                  {/* Estado del certificado */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Badge variant="success" size="sm">
                          Verificado
                        </Badge>
                        {certificate.isDownloaded && (
                          <Badge variant="info" size="sm">
                            Descargado
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-xs text-brinsa-azulOscuro/70">
                        ID: {certificate.id.slice(0, 8)}...
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <EmptyState
            icon={<Award className="w-16 h-16 text-brinsa-amarillo" />}
            title="Aún no tienes certificados"
            description="Completa todas las actividades de una categoría para obtener tu primer certificado."
            action={
              <Button>
                <a href="/home">Explorar categorías</a>
              </Button>
            }
          />
        </motion.div>
      )}

      {/* Información adicional */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <Card title="Información sobre certificados">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-brinsa-celeste mt-0.5" />
              <div>
                <h4 className="font-medium text-brinsa-azul">¿Cómo obtengo un certificado?</h4>
                <p className="text-sm text-brinsa-azulOscuro/85">
                  Completa todas las actividades de una categoría: videos, realidad aumentada, 
                  portafolio y prueba de conocimientos.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Award className="w-5 h-5 text-brinsa-amarillo mt-0.5" />
              <div>
                <h4 className="font-medium text-brinsa-azul">Validez del certificado</h4>
                <p className="text-sm text-brinsa-azulOscuro/85">
                  Todos los certificados son oficiales y están respaldados por Universidad BrinsAprende.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Download className="w-5 h-5 text-brinsa-verde mt-0.5" />
              <div>
                <h4 className="font-medium text-brinsa-azul">Descarga y uso</h4>
                <p className="text-sm text-brinsa-azulOscuro/85">
                  Puedes descargar tus certificados en formato PDF las veces que necesites 
                  y compartirlos en redes profesionales.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <ExternalLink className="w-5 h-5 text-brinsa-morado mt-0.5" />
              <div>
                <h4 className="font-medium text-brinsa-azul">Verificación</h4>
                <p className="text-sm text-brinsa-azulOscuro/85">
                  Cada certificado incluye un código único que permite verificar su autenticidad.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default CertificatesPage;
